import React, { useState, useEffect } from 'react';
import ApiService from './ApiService';

function UserRolesModal({ user, onClose, onSave }) {
  const [roles, setRoles] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      // Load all available roles
      const allRoles = await ApiService.getRoles();
      setRoles(allRoles);

      // Load user's current roles
      const currentRoles = await ApiService.getUserRoles(user.username);
      setUserRoles(currentRoles.map(ur => ur.roleId));
      setError(null);
    } catch (err) {
      console.error('Error loading roles:', err);
      setError('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRole = async (roleId) => {
    const isAssigned = userRoles.includes(roleId);
    
    try {
      setSaving(true);
      
      if (isAssigned) {
        // Remove role
        await ApiService.removeRoleFromUser(user.username, roleId);
        setUserRoles(prev => prev.filter(id => id !== roleId));
      } else {
        // Assign role
        await ApiService.assignRoleToUser(user.username, roleId);
        setUserRoles(prev => [...prev, roleId]);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error toggling role:', err);
      setError(err.message || 'Failed to update role');
      // Revert on error
      loadData();
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    onSave();
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6">
          <div className="text-gray-600">Loading roles...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-spruce-800 px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-white">Manage Roles</h2>
            <p className="text-spruce-100 text-sm mt-1">{user.name || user.email}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <div className="space-y-3">
            {roles.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No roles available. Create roles first in the Roles page.</p>
              </div>
            ) : (
              roles.map((role) => {
                const isAssigned = userRoles.includes(role.roleId);
                
                return (
                  <div
                    key={role.roleId}
                    className={`border rounded-lg p-4 transition-all ${
                      isAssigned 
                        ? 'border-spruce-500 bg-spruce-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex items-center h-6">
                        <input
                          type="checkbox"
                          checked={isAssigned}
                          onChange={() => handleToggleRole(role.roleId)}
                          disabled={saving}
                          className="w-5 h-5 text-spruce-600 rounded focus:ring-2 focus:ring-spruce-500"
                        />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-800">{role.roleName}</h3>
                        {role.description && (
                          <p className="text-sm text-gray-600 mt-1">{role.description}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Summary */}
          {userRoles.length > 0 && (
            <div className="mt-6 bg-spruce-50 border border-spruce-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-spruce-800">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">
                  {userRoles.length} role{userRoles.length !== 1 ? 's' : ''} assigned
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t bg-gray-50 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-spruce-600 text-white rounded-lg hover:bg-spruce-700"
            disabled={saving}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default UserRolesModal;